/**
 * Default add-on protection is applied ONLY to successful add operations.
 * Rendering, opening the cart, quantity changes and removals never add a plan.
 */
(() => {
  'use strict';
  if (window.mulberryAddonProtection) return;

  const originalFetch = window.fetch.bind(window);
  const sources = new Set(['add-ons', 'addons', 'pdp_addons', 'pdp_add-ons', 'cart_drawer']);
  let mainVariantProvider = null;
  let queue = Promise.resolve();
  let active = 0;

  const route = (path) => `${window.Shopify?.routes?.root || '/'}${path}`;
  const isWarranty = (item) => Boolean(item?.properties?._warranty_offer_id || item?.properties?._product || item?.vendor === 'Mulberry');
  const variantId = (item) => String(item?.variant_id || item?.id || '');
  const linked = (cart, id) => (cart.items || []).filter(item =>
    isWarranty(item) && String(item.properties?._variantId || item.properties?._variant_id || '') === String(id)
  );
  const serial = (task) => {
    const next = queue.then(task, task);
    queue = next.catch(() => {});
    return next;
  };

  async function readCart() {
    const response = await originalFetch(route('cart.js'), { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(`Unable to read cart (${response.status}).`);
    return response.json();
  }

  function parseItems(body) {
    if (!body) return [];
    if (typeof body === 'string') {
      try { return parseItems(JSON.parse(body)); } catch (_) { return parseItems(new URLSearchParams(body)); }
    }
    if (Array.isArray(body.items)) return body.items;
    if (typeof body.entries !== 'function') return body.id ? [body] : [];

    const items = new Map();
    const single = { properties: {} };
    for (const [name, value] of body.entries()) {
      if (typeof value !== 'string') continue;
      if (name === 'items') {
        try { return JSON.parse(value); } catch (_) { /* Continue with regular form fields. */ }
      }
      const match = name.match(/^items\[(\d+)\]\[(id|quantity|properties)\](?:\[([^\]]+)\])?$/);
      if (match) {
        const item = items.get(match[1]) || { properties: {} };
        if (match[2] === 'properties' && match[3]) item.properties[match[3]] = value;
        else item[match[2]] = value;
        items.set(match[1], item);
      } else if (name === 'id' || name === 'quantity') single[name] = value;
      else {
        const property = name.match(/^properties\[([^\]]+)\]$/);
        if (property) single.properties[property[1]] = value;
      }
    }
    return items.size ? [...items.values()] : single.id ? [single] : [];
  }

  function addonTargets(items) {
    const mainId = String(mainVariantProvider?.() || '');
    // A PDP batch explicitly containing the main variant can also contain
    // selected add-ons. Never assume unrelated, unmarked single adds are add-ons.
    const mainBatch = Boolean(mainId && items.length > 1 && items.some(item => variantId(item) === mainId));
    return [...new Set(items.filter(item => {
      const properties = item.properties || {};
      if (isWarranty(item) || String(properties._is_free_gift) === 'true') return false;
      if (!variantId(item) || Number(item.quantity ?? 1) <= 0) return false;
      if (mainId && variantId(item) === mainId) return false;
      const source = String(properties._added_from_location || '').toLowerCase();
      return sources.has(source) || mainBatch;
    }).map(variantId))];
  }

  function isAddRequest(input, init = {}) {
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? String(input) : input.url, window.location.href);
      const method = String(init.method || input?.method || 'GET').toUpperCase();
      return method === 'POST' && url.origin === window.location.origin && /\/cart\/add(?:\.js)?\/?$/.test(url.pathname);
    } catch (_) { return false; }
  }

  async function requestItems(input, init) {
    if (init?.body != null) return parseItems(init.body);
    if (typeof input?.clone === 'function') return parseItems(await input.clone().text());
    return [];
  }

  function report(error, id) {
    console.warn(`Mulberry 3-year add-on protection was not added${id ? ` for variant ${id}` : ''}:`, error);
    document.dispatchEvent(new CustomEvent('mulberry-addon:error', { detail: { variantId: id, message: error.message } }));
  }

  async function ready() {
    for (let attempt = 0; attempt < 40; attempt++) {
      if (window.mulberry?.core?.getWarrantyOffer && window.mulberryShop?.getWarrantyVariant && window.mulberryShop?.addWarrantyToCart) {
        await window.mulberry.core.initCompleted;
        return;
      }
      await new Promise(resolve => window.setTimeout(resolve, 200));
    }
    throw new Error('Mulberry SDK is not ready. Product remains in the cart without automatic protection.');
  }

  async function changeLine(item, quantity, properties) {
    const response = await originalFetch(route('cart/change.js'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity, ...(properties ? { properties } : {}) })
    });
    if (!response.ok) throw new Error(`Unable to update add-on protection (${response.status}).`);
  }

  async function protect(id, before) {
    let cart = await readCart();
    const parents = cart.items.filter(item => !isWarranty(item) && variantId(item) === id);
    if (!parents.length) return;
    // The SDK associates by variant, not line key. Do not attach a warranty
    // across different lines of the same variant (possibly a main-product opt-out).
    if (parents.length !== 1) throw new Error('Multiple parent lines share this variant; automatic protection was skipped to preserve their choices.');
    const parent = parents[0];
    if (String(parent.properties?._is_free_gift) === 'true') return;
    if (/white[\s-]+glove/i.test(`${parent.title || ''} ${parent.handle || ''}`)) return;
    const existing = linked(cart, id);
    if (existing.length) {
      // Preserve any existing 3Y/5Y choice; only match its quantity to the parent.
      if (existing.length === 1 && existing[0].quantity !== parent.quantity) {
        await changeLine(existing[0], parent.quantity);
      }
      return;
    }
    // An existing unprotected parent can be a deliberate opt-out. Adding more
    // of that line is not permission to resurrect a removed warranty.
    if (before.items.some(item => !isWarranty(item) && variantId(item) === id)) return;

    await ready();
    if (!parent.handle) throw new Error('Add-on product handle is unavailable.');
    const productResponse = await originalFetch(route(`products/${encodeURIComponent(parent.handle)}.json`));
    if (!productResponse.ok) throw new Error('Unable to load add-on product data.');
    const { product } = await productResponse.json();
    const variant = product?.variants?.find(item => String(item.id) === id);
    if (!variant) throw new Error('Add-on variant is unavailable in product data.');
    const offers = await window.mulberry.core.getWarrantyOffer({
      id: variant.id, external_variant_id: variant.id,
      title: product.title, price: variant.price, detail: product
    });
    const offer = Array.isArray(offers) && offers.find(item => Number(item.duration_months) === 36 && Number.isFinite(Number(item.customer_cost ?? item.cost)) && Number(item.customer_cost ?? item.cost) >= 0);
    if (!offer) return; // Ineligible or no 3Y plan: never substitute another duration.
    const resolved = await window.mulberryShop.getWarrantyVariant(offer);
    if (!resolved?.shopify_variant_id) throw new Error(resolved?.error || 'Mulberry did not resolve a Shopify warranty variant.');

    // Recheck after network calls so removal or another explicit choice wins.
    cart = await readCart();
    const currentParent = cart.items.find(item => item.key === parent.key);
    if (!currentParent || linked(cart, id).length) return;
    await window.mulberryShop.addWarrantyToCart(resolved.shopify_variant_id, {
      title: product.title, variantId: id, quantity: currentParent.quantity
    }, offer, resolved.selling_plan_id);

    cart = await readCart();
    const warranty = linked(cart, id).find(item => String(item.variant_id) === String(resolved.shopify_variant_id) && Number(item.properties?._warranty_duration_months) === 36);
    if (!warranty) throw new Error('Shopify did not confirm the add-on warranty.');
    const label = currentParent.title || currentParent.product_title || product.title;
    if (warranty.quantity !== currentParent.quantity || warranty.properties?.Product !== label) {
      await changeLine(warranty, currentParent.quantity, { ...warranty.properties, Product: label });
    }
  }

  async function repairBundledSections(response) {
    // Shopify rendered these sections before the warranty existed. Return fresh
    // sections to the original product-form code without replacing its add flow.
    try {
      const data = await response.clone().json();
      const sections = data?.sections && Object.keys(data.sections);
      if (!sections?.length) return response;
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sections.join(','));
      const updated = await originalFetch(url.toString(), { credentials: 'same-origin', cache: 'no-store' });
      if (!updated.ok) return response;
      const fresh = await updated.json();
      for (const section of sections) if (typeof fresh[section] === 'string') data.sections[section] = fresh[section];
      const headers = new Headers(response.headers);
      headers.delete('content-length');
      headers.delete('content-encoding');
      return new Response(JSON.stringify(data), { status: response.status, statusText: response.statusText, headers });
    } catch (_) { return response; }
  }

  function begin() {
    active++;
    window.__mulberryAddonProtectionInFlight = true;
  }

  function end() {
    active = Math.max(0, active - 1);
    window.__mulberryAddonProtectionInFlight = active > 0;
    if (!active) document.dispatchEvent(new CustomEvent('mulberry-addon:complete'));
  }

  async function waitForManualProtection() {
    for (let attempt = 0; attempt < 100 && window.__mulberryStep3ProtectionMutationInFlight; attempt++) {
      await new Promise(resolve => window.setTimeout(resolve, 100));
    }
    if (window.__mulberryStep3ProtectionMutationInFlight) throw new Error('Another protection change is still running.');
  }

  window.fetch = async function mulberryAddonFetch(input, init) {
    if (!isAddRequest(input, init)) return originalFetch(input, init);
    let targets;
    try { targets = addonTargets(await requestItems(input, init)); }
    catch (_) { return originalFetch(input, init); }
    if (!targets.length) return originalFetch(input, init);

    return serial(async () => {
      let before;
      try {
        await waitForManualProtection();
        before = await readCart();
      } catch (error) {
        report(error);
        return originalFetch(input, init); // Never prevent the customer's product add.
      }
      begin();
      try {
        const response = await originalFetch(input, init);
        if (!response.ok) return response;
        for (const id of targets) {
          try { await protect(id, before); } catch (error) { report(error, id); }
        }
        return await repairBundledSections(response);
      } finally { end(); }
    });
  };

  // Older PDP add-on components use async XHR. Delay send until the
  // pre-add snapshot is known, then refresh the theme when protection finishes.
  const xhrOpen = window.XMLHttpRequest.prototype.open;
  const xhrSend = window.XMLHttpRequest.prototype.send;
  window.XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this.__mwAddonRequest = { method, url, async: rest[0] !== false };
    return xhrOpen.call(this, method, url, ...rest);
  };
  window.XMLHttpRequest.prototype.send = function(body) {
    const request = this.__mwAddonRequest;
    const targets = request?.async && isAddRequest(request.url, request) ? addonTargets(parseItems(body)) : [];
    if (!targets.length) return xhrSend.call(this, body);
    const xhr = this;
    serial(async () => {
      let before;
      try {
        await waitForManualProtection();
        before = await readCart();
      } catch (error) {
        report(error);
        if (xhr.readyState === 1) xhrSend.call(xhr, body);
        return;
      }
      // An abort while waiting must not turn into a later unexpected add.
      if (xhr.readyState !== 1) return;
      begin();
      try {
        await new Promise((resolve, reject) => {
          xhr.addEventListener('loadend', resolve, { once: true });
          try { xhrSend.call(xhr, body); } catch (error) { reject(error); }
        });
        if (xhr.status < 200 || xhr.status >= 300) return;
        for (const id of targets) {
          try { await protect(id, before); } catch (error) { report(error, id); }
        }
      } finally { end(); }
    }).catch(error => report(error));
  };

  window.mulberryAddonProtection = {
    setMainVariantProvider(provider) { mainVariantProvider = provider; },
    whenIdle() { return queue; },
    isBusy() { return active > 0; }
  };
})();
/* End shared add-on protection bootstrap. */
(() => {
  'use strict';
  const state = {
    initialized: false,
    initInFlight: null,
    renderInFlight: false,
    queuedRefresh: false,
    listenersBound: false,
    drawerObserver: null,
    drawerWasActive: false,
    warrantyRemovalTimer: null,
    interceptorsBound: false,
    mutationInspectTimer: null,
    mutationInspectInFlight: false,
    mutationInspectQueued: false,
    lastCartSnapshot: null,
    handlingWarrantyMutation: false,
    warrantyRenderTimer: null,
  };
  function getCartSignature(cart) {
    if (!cart || !Array.isArray(cart.items)) return '';
    const lineState = cart.items
      .map((item) => `${item.key || item.id}:${item.quantity}`)
      .sort()
      .join('|');
    return `${cart.item_count || 0}::${cart.total_price || 0}::${lineState}`;
  }
  function getCartSnapshot(cart) {
    return {
      signature: getCartSignature(cart),
      totalPrice: Number(cart?.total_price) || 0,
      warrantyCount: Array.isArray(cart?.items)
        ? cart.items.filter((item) => item?.properties?._warranty_offer_id != null || item?.vendor === 'Mulberry').length
        : 0,
      parentSignature: Array.isArray(cart?.items)
        ? cart.items
            .filter((item) => item?.properties?._warranty_offer_id == null && item?.vendor !== 'Mulberry')
            .map((item) => `${item.key || item.id}:${item.quantity}`)
            .sort()
            .join('|')
        : ''
    };
  }
  async function fetchCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    return response.json();
  }
  async function syncCartSnapshot() {
    try {
      const cart = await fetchCart();
      state.lastCartSnapshot = getCartSnapshot(cart);
    } catch (_) {
    }
  }
  function isCartMutationUrl(rawUrl) {
    if (!rawUrl) return false;
    try {
      const url = new URL(rawUrl, window.location.origin);
      return [
        '/cart/change',
        '/cart/update',
        '/cart/add',
        '/cart/clear'
      ].some((path) => url.pathname.includes(path));
    } catch (_) {
      return false;
    }
  }
  function getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const node = parsed.querySelector(selector);
    return node ? node.innerHTML : '';
  }
  function getThemeSectionsToRefresh() {
    const sections = [];
    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems?.dataset?.id) {
      sections.push({
        section: mainCartItems.dataset.id,
        targetId: 'main-cart-items',
        sourceSelector: '.js-contents',
        targetSelector: '.js-contents'
      });
    }
    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter?.dataset?.id) {
      sections.push({
        section: mainCartFooter.dataset.id,
        targetId: 'main-cart-footer',
        sourceSelector: '.js-contents',
        targetSelector: '.js-contents'
      });
    }
    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        section: 'cart-icon-bubble',
        targetId: 'cart-icon-bubble',
        sourceSelector: '.shopify-section'
      });
    }
    if (document.getElementById('cart-live-region-text')) {
      sections.push({
        section: 'cart-live-region-text',
        targetId: 'cart-live-region-text',
        sourceSelector: '.shopify-section'
      });
    }
    if (document.querySelector('#CartDrawer')) {
      sections.push({
        section: 'cart-drawer',
        targetSelector: '#CartDrawer',
        sourceSelector: '#CartDrawer'
      });
    }
    return sections;
  }
  async function refreshThemeCartSections() {
    const sections = getThemeSectionsToRefresh();
    if (!sections.length) return;
    const sectionList = sections.map((entry) => entry.section).join(',');
    const cartUrl = (typeof window.routes !== 'undefined' && window.routes.cart_url) ? window.routes.cart_url : '/cart';
    const res = await fetch(`${cartUrl}?sections=${sectionList}`);
    if (!res.ok) return;
    const sectionData = await res.json();
    sections.forEach((entry) => {
      const target = entry.targetId
        ? document.getElementById(entry.targetId)
        : document.querySelector(entry.targetSelector);
      const html = sectionData?.[entry.section];
      if (!target || !html) return;
      const replacement = getSectionInnerHTML(html, entry.sourceSelector);
      if (!replacement) return;
      const destination = entry.targetSelector && entry.targetId
        ? target.querySelector(entry.targetSelector) || target
        : target;
      destination.innerHTML = replacement;
    });
    if (typeof window.emit === 'function') {
      window.emit('update:cart-line-item');
    }
  }
  async function inspectCartMutation() {
    if (state.mutationInspectInFlight) {
      state.mutationInspectQueued = true;
      return;
    }
    if (state.handlingWarrantyMutation) {
      return;
    }
    if (window.__mulberryStep3ProtectionMutationInFlight || window.__mulberryAddonProtectionInFlight) {
      scheduleWarrantyRemovalSync();
      return;
    }
    state.mutationInspectInFlight = true;
    try {
      const previousSnapshot = state.lastCartSnapshot;
      const cart = await fetchCart();
      const nextSnapshot = getCartSnapshot(cart);
      state.lastCartSnapshot = nextSnapshot;
      if (!previousSnapshot) {
        return;
      }
      const hasWarranty = previousSnapshot.warrantyCount > 0 || nextSnapshot.warrantyCount > 0;
      const parentChanged = previousSnapshot.parentSignature !== nextSnapshot.parentSignature;
      const warrantyRemoved = previousSnapshot.warrantyCount > nextSnapshot.warrantyCount;
      if (!hasWarranty || (!parentChanged && !warrantyRemoved)) {
        return;
      }
      await removeAndSync();
      await refreshThemeCartSections();
      await renderCart({ refresh: true });
      await syncCartSnapshot();
    } finally {
      state.mutationInspectInFlight = false;
      if (state.mutationInspectQueued) {
        state.mutationInspectQueued = false;
        inspectCartMutation();
      }
    }
  }
  function scheduleWarrantyRemovalSync() {
    window.clearTimeout(state.mutationInspectTimer);
    state.mutationInspectTimer = window.setTimeout(() => {
      inspectCartMutation();
    }, 100);
  }
  function bindMutationInterceptors() {
    if (state.interceptorsBound) return;
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const [input, init] = args;
      const url = typeof input === 'string' ? input : input?.url;
      const method = String(init?.method || input?.method || 'GET').toUpperCase();
      const response = await originalFetch(...args);
      if (method !== 'GET' && isCartMutationUrl(url)) {
        scheduleWarrantyRemovalSync();
      }
      return response;
    };
    const xhrOpen = window.XMLHttpRequest.prototype.open;
    const xhrSend = window.XMLHttpRequest.prototype.send;
    window.XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
      this.__mulberryMethod = String(method || 'GET').toUpperCase();
      this.__mulberryUrl = url;
      return xhrOpen.call(this, method, url, ...rest);
    };
    window.XMLHttpRequest.prototype.send = function patchedSend(...args) {
      this.addEventListener('loadend', () => {
        if (this.__mulberryMethod !== 'GET' && isCartMutationUrl(this.__mulberryUrl)) {
          scheduleWarrantyRemovalSync();
        }
      }, { once: true });
      return xhrSend.apply(this, args);
    };
    state.interceptorsBound = true;
  }
  function canUseMulberryCart() {
    return Boolean(
      window.mulberryShop &&
      window.mulberryShop.cart &&
      typeof window.mulberryShop.cart.init === 'function' &&
      typeof window.mulberryShop.cart.render === 'function'
    );
  }
  async function initCart() {
    if (state.initialized) return true;
    if (state.initInFlight) return state.initInFlight;
    if (!canUseMulberryCart()) return false;
    state.initInFlight = (async () => {
      await window.mulberryShop.cart.init();
      state.initialized = true;
      return true;
    })();
    try {
      return await state.initInFlight;
    } finally {
      state.initInFlight = null;
    }
  }
  async function renderCart(options = {}) {
    const refresh = Boolean(options?.refresh);
    if (!canUseMulberryCart()) return;
    if (window.__mulberryAddonProtectionInFlight) {
      state.queuedRefresh = true;
      return;
    }
    if (state.renderInFlight) {
      state.queuedRefresh = state.queuedRefresh || refresh;
      return;
    }
    state.renderInFlight = true;
    try {
      const ready = await initCart();
      if (!ready) return;
      if (refresh) {
        await window.mulberryShop.cart.render({ refresh: true });
      } else {
        await window.mulberryShop.cart.render();
      }
      await syncCartSnapshot();
    } catch (error) {
      console.error('Mulberry cart render failed:', error);
    } finally {
      state.renderInFlight = false;
      if (state.queuedRefresh) {
        state.queuedRefresh = false;
        renderCart({ refresh: true });
      }
    }
  }
  function bindRefreshTriggers() {
    if (state.listenersBound) return;
    document.addEventListener('mulberry-addon:complete', () => {
      // Run after the original add handler consumes its refreshed sections.
      window.setTimeout(async () => {
        try {
          await refreshThemeCartSections();
          await renderCart({ refresh: true });
        } catch (error) { console.warn('Add-on protection cart refresh failed:', error); }
      }, 0);
    });
    document.addEventListener('update:cart-line-item', () => {
      if (window.__mulberryStep3ProtectionMutationInFlight || window.__mulberryAddonProtectionInFlight) return;
      renderCart({ refresh: true });
    });
    document.addEventListener('mulberry-shopify:warranty-added', async () => {
      if (window.__mulberryStep3ProtectionMutationInFlight || window.__mulberryAddonProtectionInFlight) return;
      await refreshThemeCartSections();
      await renderCart({ refresh: true });
    });
    const drawer = document.querySelector('cart-drawer');
    if (drawer && !state.drawerObserver) {
      state.drawerWasActive = drawer.classList.contains('active');
      state.drawerObserver = new MutationObserver((mutations) => {
        const isActive = drawer.classList.contains('active');
        const hasNewWarrantyMount = mutations.some((mutation) => {
          if (mutation.type !== 'childList') return false;
          return Array.from(mutation.addedNodes).some((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return false;
            return (
              node.matches?.('warranty-details') ||
              node.querySelector?.('warranty-details')
            );
          });
        });
        if (
          (isActive && !state.drawerWasActive) ||
          hasNewWarrantyMount
        ) {
          window.clearTimeout(state.warrantyRenderTimer);
          state.warrantyRenderTimer = window.setTimeout(() => {
            renderCart({ refresh: true });
          }, 4000);
        }
        state.drawerWasActive = isActive;
      });
      state.drawerObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true
      });
    }
    state.listenersBound = true;
  }
  async function bootstrapCartPlacement() {
    bindMutationInterceptors();
    bindRefreshTriggers();
    await initCart();
    await renderCart();
    const drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.classList.contains('active')) {
      await renderCart({ refresh: true });
    }
  }
  // Theme-side cleanup that works WITHOUT the Mulberry SDK (mulberry.js only
  // loads on product templates, so window.mulberryRemoveAndSyncWarranties is
  // undefined everywhere else). A warranty line left alone in the cart is
  // invisible — the drawer renders it display:none — but still counts toward
  // the header cart badge, so the badge shows items the shopper can't see.
  // When every remaining line is such a service line (Mulberry warranty or
  // White Glove Delivery), remove them all. Returns true when lines were
  // removed so callers can refresh the theme sections.
  async function removeStrandedServiceLines() {
    try {
      const cart = await fetchCart();
      const items = Array.isArray(cart.items) ? cart.items : [];
      if (!items.length) return false;
      const isWarrantyLine = (item) =>
        item?.properties?._warranty_offer_id != null ||
        item?.properties?._product != null ||
        item?.vendor === 'Mulberry';
      const isWhiteGloveLine = (item) =>
        String(item?.handle || '').indexOf('white-glove') !== -1 ||
        String(item?.title || '').indexOf('White Glove') !== -1;
      const allService = items.every((item) => isWarrantyLine(item) || isWhiteGloveLine(item));
      if (!allService) return false;
      const updates = {};
      items.forEach((item) => {
        updates[item.key] = 0;
      });
      await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      return true;
    } catch (_) {
      return false;
    }
  }
  async function removeAndSync() {
    if (window.__mulberryAddonProtectionInFlight) return;
    state.handlingWarrantyMutation = true;
    if (typeof window.mulberryRemoveAndSyncWarranties === 'function') {
      try {
        await window.mulberryRemoveAndSyncWarranties();
        await removeStrandedServiceLines();
        await refreshThemeCartSections();
      } finally {
        state.handlingWarrantyMutation = false;
      }
      return;
    }
    try {
      await removeStrandedServiceLines();
      await refreshThemeCartSections();
      await renderCart({ refresh: true });
    } finally {
      state.handlingWarrantyMutation = false;
    }
  }
  async function refreshMulberryCart() {
    if (window.__mulberryAddonProtectionInFlight) return;
    if (
      !window.mulberryShop?.cart?.init ||
      !window.mulberryShop?.cart?.render
    ) {
      return;
    }
    await new Promise(resolve => setTimeout(resolve, 350));
    const mounts = document.querySelectorAll('warranty-details');
    if (!mounts.length) {
      return;
    }
    await window.mulberryShop.cart.init();
    await new Promise(resolve => requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    }));
    await window.mulberryShop.cart.render();
  }
  window.mulberryCartPlacement = {
    init: initCart,
    render: renderCart,
    refresh: refreshMulberryCart,
    removeAndSync
  };
  function startWhenReady() {
    const start = () => {
      bootstrapCartPlacement();
    };
    const register = () => {
      // Self-heal carts already stuck with only hidden service lines (e.g. a
      // warranty stranded by a drawer removal on a page without the SDK):
      // the server-rendered badge shows a count, but the drawer would show
      // nothing. Only runs when the badge is visible, i.e. cart not empty.
      const bubble = document.getElementById('cart-icon-bubble');
      const cartHasItems = Boolean(bubble && bubble.querySelector('div'));
      if (cartHasItems) {
        removeStrandedServiceLines().then((removed) => {
          if (removed) refreshThemeCartSections();
        });
      }
      if (canUseMulberryCart()) {
        start();
        return;
      }
      // No Mulberry SDK on this page (mulberry.js only loads on product
      // templates). Still watch cart mutations so a removal here can't
      // strand hidden warranty lines in the cart. Skip the cart read when
      // the bubble shows an empty cart (TTDEV-800).
      bindMutationInterceptors();
      if (cartHasItems) syncCartSnapshot();
      document.addEventListener(
        'mulberry-shopify:loaded',
        start,
        { once: true }
      );
    };
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        register,
        { once: true }
      );
    } else {
      register();
    }
  }
  startWhenReady();
})();